import {
  exportReportToExcel,
  exportReportToPDF,
  type ReportExportOptions,
} from '../../utils/reportExport';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import type {
  MgaBondedLine,
  MgaCashEntry,
  MgaCrewLine,
  MgaCrewRequest,
  MgaPeriod,
} from './useData';

function n(v: number | null | undefined) {
  return Number.isFinite(Number(v)) ? Number(v) : 0;
}

function round2(v: number) {
  return Math.round(v * 100) / 100;
}

function money(v: number | null | undefined) {
  return round2(n(v));
}

export type MgaExportPayload = {
  period: MgaPeriod;
  cash: MgaCashEntry[];
  bonded: MgaBondedLine[];
  requests: MgaCrewRequest[];
};

function periodTitle(period: MgaPeriod) {
  const vessel = period.vessel?.vesselName ?? `Vessel #${period.vesselId}`;
  return `MGA — ${vessel} — ${formatDateGlobal(period.periodFrom)} → ${formatDateGlobal(period.periodTo)}`;
}

function fileSlug(period: MgaPeriod) {
  const abrv = (period.vessel?.vesselAbrv || period.vessel?.vesselName || 'vessel')
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 40);
  const from = String(period.periodFrom).slice(0, 10);
  return `MGA_${abrv}_${from}`;
}

function summaryTable(payload: MgaExportPayload) {
  const { period, cash, bonded, requests } = payload;
  const ccy = period.currency || 'USD';
  const cashIn = cash
    .filter((e) => e.entryType === 'INCOME' || e.entryType === 'CTM')
    .reduce((s, e) => s + n(e.amount), 0);
  const cashOut = cash.filter((e) => e.entryType === 'EXPENSE').reduce((s, e) => s + n(e.amount), 0);
  const ctm = cash.filter((e) => e.entryType === 'CTM').reduce((s, e) => s + n(e.amount), 0);
  const bondedIn = bonded
    .filter((l) => l.section === 'STOCK' || l.section === 'INCOMING')
    .reduce((s, r) => s + n(r.amount), 0);
  const bondedOut = bonded.filter((l) => l.section === 'EXPENSE').reduce((s, r) => s + n(r.amount), 0);
  const cashByMaster = requests
    .filter((r) => r.requestType === 'CASH_BY_MASTER')
    .reduce((s, r) => s + n(r.amount), 0);
  const allotments = requests
    .filter((r) => r.requestType === 'ALLOTMENT')
    .reduce((s, r) => s + n(r.amount), 0);

  return {
    headers: ['Section', 'Metric', 'Amount', 'Currency'],
    rows: [
      ['Period', 'Status', period.status, ''],
      ['Period', 'Crew lines', period.totals?.lineCount ?? period.lines?.length ?? 0, ''],
      ['Payroll', 'Gross payable', money(period.totals?.grossPayable), ccy],
      ['Payroll', 'OT total', money(period.totals?.otTotal), ccy],
      ['Payroll', 'Extra earnings', money(period.totals?.extraEarnings), ccy],
      ['Payroll', 'Previous balance', money(period.totals?.previousBalance), ccy],
      ['Payroll', 'Total deductions', money(period.totals?.totalDeductions ?? period.totals?.deductions), ccy],
      ['Payroll', 'Allotments (ALLOT)', money(period.totals?.allotmentAmount), ccy],
      ['Payroll', 'Net due', money(period.totals?.netDue), ccy],
      ['Cash / CTM', 'CTM remittances', money(ctm), ccy],
      ['Cash / CTM', 'Cash received (incl. CTM)', money(cashIn), ccy],
      ['Cash / CTM', 'Cash paid out', money(cashOut), ccy],
      ['Cash / CTM', 'Cash on hand', money(cashIn - cashOut), ccy],
      ['Bonded', 'Incoming / stock', money(bondedIn), ccy],
      ['Bonded', 'Issues (expense)', money(bondedOut), ccy],
      ['Bonded', 'Balance', money(bondedIn - bondedOut), ccy],
      ['Requests', 'Cash by master', money(cashByMaster), ccy],
      ['Requests', 'Allotments', money(allotments), ccy],
    ] as (string | number)[][],
  };
}

function payrollTable(lines: MgaCrewLine[]) {
  return {
    headers: [
      'Crew',
      'Rank',
      'Days',
      'OT hrs',
      'OT rate',
      'OT total',
      'Prev.',
      'Extra',
      'Master adv.',
      'Office adv.',
      'Allotment',
      'Cig.',
      'Internet',
      'Bonded',
      'Various',
      'Gross',
      'Deductions',
      'Net due',
    ],
    rows: lines.map((l) => [
      l.crewName,
      l.rankName || '',
      money(l.daysInPeriod),
      money(l.overtimeHours),
      money(l.overtimeRate),
      money(l.otTotal ?? l.overtimeHours * l.overtimeRate),
      money(l.previousBalance),
      money(l.extraEarnings),
      money(l.cashAdvanceMaster),
      money(l.cashAdvanceOffice),
      money(l.allotmentAmount),
      money(l.cigarettesAmount),
      money(l.internetCardsAmount),
      money(l.otherBondedAmount),
      money(l.variousDeductions),
      money(l.grossPayable),
      money(l.totalDeductions ?? l.deductions),
      money(l.netDue),
    ]),
  };
}

function cashTable(cash: MgaCashEntry[]) {
  return {
    headers: ['Type', 'Date', 'Description', 'Crew', 'Currency', 'Amount', 'Remarks'],
    rows: cash.map((e) => [
      e.entryType,
      e.entryDate ? formatDateGlobal(e.entryDate) : '',
      e.description,
      e.crewLine?.crewName || '',
      e.currency || '',
      money(e.amount),
      e.remarks || '',
    ]),
  };
}

function bondedTable(bonded: MgaBondedLine[]) {
  return {
    headers: ['Section', 'Date', 'Description', 'Crew', 'Unit price', 'Qty', 'Amount', 'Remarks'],
    rows: bonded.map((l) => [
      l.section,
      l.entryDate ? formatDateGlobal(l.entryDate) : '',
      l.description,
      l.crewLine?.crewName || '',
      money(l.unitPrice),
      money(l.qty),
      money(l.amount),
      l.remarks || '',
    ]),
  };
}

function requestsTable(requests: MgaCrewRequest[]) {
  return {
    headers: ['Type', 'Date', 'Crew', 'Amount', 'Remarks'],
    rows: requests.map((r) => [
      r.requestType,
      r.entryDate ? formatDateGlobal(r.entryDate) : '',
      r.crewLine?.crewName || '',
      money(r.amount),
      r.remarks || '',
    ]),
  };
}

function buildOptions(payload: MgaExportPayload): ReportExportOptions {
  const title = periodTitle(payload.period);
  const summary = summaryTable(payload);
  const payroll = payrollTable(payload.period.lines ?? []);
  const cash = cashTable(payload.cash);
  const bonded = bondedTable(payload.bonded);
  const requests = requestsTable(payload.requests);

  return {
    reportTitle: title,
    fileName: fileSlug(payload.period),
    table: summary,
    rightAlignColumnIndices: [2],
    additionalExcelSheets: [
      {
        name: 'PAYROLL',
        sheetTitle: `${title} — Payroll`,
        table: payroll,
        rightAlignColumnIndices: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
      },
      {
        name: 'CASH',
        sheetTitle: `${title} — Cash / CTM`,
        table: cash,
        rightAlignColumnIndices: [5],
      },
      {
        name: 'BONDED',
        sheetTitle: `${title} — Bonded store`,
        table: bonded,
        rightAlignColumnIndices: [4, 5, 6],
      },
      {
        name: 'REQUESTS',
        sheetTitle: `${title} — Crew requests`,
        table: requests,
        rightAlignColumnIndices: [3],
      },
    ],
    additionalPdfSections: [
      {
        sectionTitle: 'Payroll',
        table: payroll,
        rightAlignColumnIndices: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
      },
      {
        sectionTitle: 'Cash / CTM',
        table: cash,
        rightAlignColumnIndices: [5],
      },
      {
        sectionTitle: 'Bonded store',
        table: bonded,
        rightAlignColumnIndices: [4, 5, 6],
      },
      {
        sectionTitle: 'Crew requests',
        table: requests,
        rightAlignColumnIndices: [3],
      },
    ],
  };
}

export async function exportMgaPeriodExcel(payload: MgaExportPayload) {
  await exportReportToExcel(buildOptions(payload));
}

export async function exportMgaPeriodPdf(payload: MgaExportPayload) {
  await exportReportToPDF(buildOptions(payload));
}
